import { useT } from '../i18n/I18nProvider';
import type { ScreenModel } from '../domain/resolveScreen';
import { TRANSFERS } from '../domain/transfers';
import type { TransferDirection } from '../domain/types';
import { Icon } from './icons';

interface Props {
  model: ScreenModel;
  /** Hlášení už odešlo – tlačítko zezelená a zamkne se. */
  reported: boolean;
  /** Převoz už byl potvrzen v této relaci. */
  transferConfirmed: boolean;
  transferDirection: TransferDirection | null;
  onScanNext: () => void;
  onReport: () => void;
  onFindManually: () => void;
  onConfirmTransfer: () => void;
}

/**
 * Sticky akční lišta.
 *
 * U vrstvy STOP je hierarchie tlačítek ZÁMĚRNĚ prohozená: „Nahlásit“ je bílé
 * a primární, „Skenovat další“ jen obtahované. Cíl je, aby skladník z návyku
 * „ťuk-ťuk-ťuk“ nepřeklikl chybu dál. Prosím nezjednodušovat zpátky.
 */
export function ActionBar({
  model,
  reported,
  transferConfirmed,
  transferDirection,
  onScanNext,
  onReport,
  onFindManually,
  onConfirmTransfer,
}: Props) {
  const { t } = useT();

  if (model.actions.variant === 'stop') {
    return (
      <div className="actions">
        <button
          className={`btn report${reported ? ' done' : ''}`}
          type="button"
          disabled={reported}
          onClick={onReport}
        >
          <Icon name={reported ? 'check' : 'siren'} />
          {reported ? t('reported') : t('report')}
        </button>
        <button className="btn ghost" type="button" onClick={onScanNext}>
          <Icon name="scan" />
          {t('scanNext')}
        </button>
      </div>
    );
  }

  // Prototyp měl u NOTFOUND jediné tlačítko „Najít manuálně“, jenže tím se
  // obrazovka stala slepou uličkou – z nenalezeného kódu nevedla cesta zpět
  // ke skeneru. „Skenovat další“ je proto jen obtahované, aby zůstalo jasné,
  // že primární úmysl je zásilku dohledat, ne kód přeskočit.
  if (model.actions.variant === 'notfound') {
    return (
      <div className="actions">
        <button className="btn primary" type="button" onClick={onFindManually}>
          <Icon name="find" />
          {t('findManual')}
        </button>
        <button className="btn ghost" type="button" onClick={onScanNext}>
          <Icon name="scan" />
          {t('scanNext')}
        </button>
      </div>
    );
  }

  if (model.actions.variant === 'transfer') {
    // Popup potvrzení otevírají OBĚ tlačítka – tap na navyklé tlačítko
    // skladníkovi rovnou řekne, co se po něm chce.
    return (
      <div className="actions">
        <button className="btn primary" type="button" onClick={onConfirmTransfer}>
          <Icon name="border" />
          {t('confirmTr')}
        </button>
        <button className="btn ghost" type="button" onClick={onConfirmTransfer}>
          <Icon name="scan" />
          {t('scanNext')}
        </button>
      </div>
    );
  }

  // Po potvrzení převozu zůstává zelené tlačítko jako zpětná vazba, že se
  // potvrzení opravdu zapsalo, a skenování se odemkne.
  return (
    <div className="actions">
      {transferConfirmed && transferDirection && (
        <button className="btn report done" type="button" disabled>
          <Icon name="check" />
          {t(TRANSFERS[transferDirection].valueKey)}
        </button>
      )}
      <button className="btn primary" type="button" onClick={onScanNext}>
        <Icon name="scan" />
        {t('scanNext')}
      </button>
    </div>
  );
}
